import { inject, observer } from 'mobx-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from '../../t';
import { useSwipeable } from 'react-swipeable';
import C from './colors';
import { getPictureOriginalUrl, getPicturePreviewUrl } from '../../pictureUrls';

function Heart({ filled }) {
    return (
        <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
            <path
                d="M8 13.5C8 13.5 1.5 9.5 1.5 5.5C1.5 3.5 3 2 5 2C6.5 2 7.5 3 8 4C8.5 3 9.5 2 11 2C13 2 14.5 3.5 14.5 5.5C14.5 9.5 8 13.5 8 13.5Z"
                fill={filled ? '#fff' : 'none'}
                stroke="#fff"
                strokeWidth="1.5"
                strokeLinejoin="round"
            />
        </svg>
    );
}

const CustomerImageDialog = inject('snackbarStore')(observer(({
    open,
    setOpen,
    currentIndex,
    setCurrentIndex,
    previewPictures,
    handlePictureSelect,
    snackbarStore,
    selectedPicturesIds,
    photoCount,
    albumClaimed,
    watermark,
    tagDefinitions = [],
    customerTagAssignments = {},
    onTagToggle,
}) => {
    const { t } = useTranslation();

    const isCurrentSelected = useMemo(() =>
        selectedPicturesIds.includes(previewPictures[currentIndex]?.id),
        [previewPictures, currentIndex, selectedPicturesIds]
    );

    const handleNext = useCallback(() => {
        if (currentIndex < previewPictures.length - 1) {
            setCurrentIndex(currentIndex + 1);
        }
    }, [previewPictures, currentIndex, setCurrentIndex]);

    const handlePrev = useCallback(() => {
        if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
        }
    }, [currentIndex, setCurrentIndex]);

    const handleToggle = useCallback(() => {
        const p = previewPictures[currentIndex];
        if (p && !albumClaimed) {
            handlePictureSelect(p.id, !isCurrentSelected);
        }
    }, [previewPictures, currentIndex, isCurrentSelected, handlePictureSelect, albumClaimed]);

    const [imgLoaded, setImgLoaded] = useState(false);

    // Reset loading state whenever the photo changes
    useEffect(() => { setImgLoaded(false); }, [currentIndex]);

    const handlers = useSwipeable({ onSwipedLeft: handleNext, onSwipedRight: handlePrev });

    useEffect(() => {
        if (!open) return;
        const onKey = e => {
            if (e.key === 'ArrowLeft') handlePrev();
            else if (e.key === 'ArrowRight') handleNext();
            else if (e.key === ' ') handleToggle();
            e.preventDefault();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, handlePrev, handleNext, handleToggle]);

    if (!open) return null;

    const p = previewPictures[currentIndex];
    const total = previewPictures.length;

    const navBtn = (side, disabled, onClick, label) => (
        <button
            onClick={onClick}
            disabled={disabled}
            style={{
                position: 'absolute', top: '50%', [side]: 12,
                transform: 'translateY(-50%)',
                width: 40, height: 40, borderRadius: 20, border: 'none',
                background: 'rgba(255,255,255,.08)', color: disabled ? 'rgba(255,255,255,.2)' : '#fff',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                cursor: disabled ? 'default' : 'pointer',
                fontSize: 24, lineHeight: 1,
                display: 'grid', placeItems: 'center',
            }}
        >{label}</button>
    );

    return (
        <div data-testid="customer-image-viewer" style={{
            position: 'fixed', inset: 0, zIndex: 1300,
            background: '#0a0908', color: '#fff',
            display: 'flex', flexDirection: 'column',
            fontFamily: C.sans,
            WebkitFontSmoothing: 'antialiased',
        }}>
            {/* top bar */}
            <div style={{
                padding: '60px 16px 14px',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                flexShrink: 0,
            }}>
                <button
                    data-testid="customer-viewer-close"
                    onClick={() => setOpen(false)}
                    style={{
                        width: 36, height: 36, borderRadius: 18, border: 'none',
                        background: 'rgba(255,255,255,.1)', color: '#fff',
                        backdropFilter: 'blur(8px)',
                        WebkitBackdropFilter: 'blur(8px)',
                        cursor: 'pointer', fontSize: 18,
                        display: 'grid', placeItems: 'center',
                    }}>×</button>
                <div style={{ fontFamily: C.serif, fontSize: 15, fontVariantNumeric: 'tabular-nums' }}>
                    {currentIndex + 1} <span style={{ opacity: 0.5 }}>/ {total}</span>
                </div>
                <div style={{ width: 36 }} />
            </div>

            {/* photo area */}
            <div
                {...handlers}
                style={{
                    flex: 1, position: 'relative',
                    display: 'grid', placeItems: 'center', padding: 8,
                    overflow: 'hidden',
                }}
            >
                <div style={{
                    width: '100%', maxHeight: '100%',
                    aspectRatio: '4/5',
                    borderRadius: 6, overflow: 'hidden', position: 'relative',
                    background: '#1a1614',
                }}>
                    {/* blurred thumbnail placeholder */}
                    {getPicturePreviewUrl(p) && (
                        <img
                            src={getPicturePreviewUrl(p)}
                            alt=""
                            style={{
                                position: 'absolute', inset: 0,
                                width: '100%', height: '100%',
                                objectFit: 'contain',
                                filter: 'blur(12px)',
                                transform: 'scale(1.05)',
                                opacity: imgLoaded ? 0 : 1,
                                transition: 'opacity .3s ease',
                            }}
                        />
                    )}

                    {/* loading spinner */}
                    {!imgLoaded && (
                        <div style={{
                            position: 'absolute', inset: 0,
                            display: 'grid', placeItems: 'center',
                            pointerEvents: 'none',
                        }}>
                            <div style={{
                                width: 28, height: 28,
                                border: '2px solid rgba(255,255,255,.15)',
                                borderTopColor: 'rgba(255,255,255,.7)',
                                borderRadius: '50%',
                                animation: 'spin .8s linear infinite',
                            }} />
                        </div>
                    )}

                    {/* full-res image */}
                    {p?.publicUrl && (
                        <img
                            key={p.publicUrl}
                            src={getPictureOriginalUrl(p)}
                            alt={p.fileName}
                            onLoad={() => setImgLoaded(true)}
                            style={{
                                position: 'absolute', inset: 0,
                                width: '100%', height: '100%',
                                objectFit: 'contain', objectPosition: 'center',
                                opacity: imgLoaded ? 1 : 0,
                                transition: 'opacity .4s ease',
                            }}
                        />
                    )}

                    {watermark && (
                        <div style={{
                            position: 'absolute', inset: 0,
                            color: 'rgba(255,255,255,.3)',
                            fontSize: 38, fontWeight: 600, letterSpacing: 8,
                            transform: 'rotate(-18deg)',
                            display: 'grid', placeItems: 'center',
                            fontFamily: C.serif,
                            pointerEvents: 'none',
                        }}>图派</div>
                    )}
                </div>

                {navBtn('left', currentIndex === 0, handlePrev, '‹')}
                {navBtn('right', currentIndex === total - 1, handleNext, '›')}
            </div>

            {/* bottom actions */}
            <div style={{ padding: '14px 18px 24px', flexShrink: 0 }}>
                {p && (
                    <div style={{ fontFamily: 'ui-monospace, monospace', fontSize: 11, color: 'rgba(255,255,255,.5)', marginBottom: tagDefinitions.length > 0 ? 10 : 14 }}>
                        {p.fileName}
                    </div>
                )}
                {tagDefinitions.length > 0 && !albumClaimed && (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
                        {tagDefinitions.map((tag, i) => {
                            const applied = (customerTagAssignments[p?.id] || []).includes(tag.id);
                            return (
                                <button
                                    key={tag.id}
                                    data-testid={`customer-photo-tag-${p?.id}-${i}`}
                                    data-tag-id={tag.id}
                                    data-active={applied ? 'true' : 'false'}
                                    onClick={() => onTagToggle?.(p?.id, tag.id)}
                                    style={{
                                        padding: '6px 14px', borderRadius: 20, border: 'none',
                                        background: applied ? (tag.color || C.brand) : 'rgba(255,255,255,.12)',
                                        color: applied ? '#fff' : 'rgba(255,255,255,.65)',
                                        fontFamily: C.sans, fontSize: 13, fontWeight: applied ? 500 : 400,
                                        cursor: 'pointer',
                                        transition: 'all .15s',
                                        boxShadow: applied ? `0 4px 12px ${tag.color || C.brand}55` : 'none',
                                    }}>
                                    {tag.name}
                                </button>
                            );
                        })}
                    </div>
                )}
                <div style={{ display: 'flex', gap: 10 }}>
                    {!albumClaimed && (
                        <button
                            data-testid={`customer-viewer-toggle-${p.id}`}
                            aria-pressed={isCurrentSelected}
                            onClick={handleToggle}
                            style={{
                                flex: 1, padding: 14,
                                background: isCurrentSelected ? C.brand : 'rgba(255,255,255,.1)',
                                color: '#fff', border: 'none', borderRadius: 10,
                                fontFamily: C.sans, fontSize: 15, fontWeight: 500,
                                cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                            }}
                        >
                            <Heart filled={isCurrentSelected} />
                            {isCurrentSelected ? '已加入选片' : '加入选片'}
                        </button>
                    )}
                    {albumClaimed && (
                        <div style={{
                            flex: 1, padding: 14, borderRadius: 10,
                            background: 'rgba(255,255,255,.08)',
                            color: 'rgba(255,255,255,.5)',
                            fontFamily: C.sans, fontSize: 14, textAlign: 'center',
                        }}>
                            {isCurrentSelected ? '✓ 已选' : '未选'}
                        </div>
                    )}
                    <button style={{
                        width: 50, padding: 14,
                        background: 'rgba(255,255,255,.1)', border: 'none', borderRadius: 10,
                        color: '#fff', fontSize: 16, cursor: 'pointer',
                    }} title="留言">💬</button>
                </div>
            </div>
        </div>
    );
}));

export { CustomerImageDialog };
